import qs from 'query-string';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type {
	AssetEntity,
	AssetOption,
	AssetType,
	FieldFilter,
	SuggestionsResponse,
} from '../../common/types.tsx';

export const mapAssetEntityToAssetOptions = (data: AssetEntity[]): AssetOption[] =>
	data.map(({ label, origin }) => ({
		label: label.value, // readable name
		value: `${origin.appKey}/${origin.originId}`, // id
	}));

/*
 * Fetch the filters attached to a custom field.
 * Internal API from com.atlassian.servicedesk.plugins.assets.internal.rest.AssetFieldResource
 * */
export const fetchFieldFilterNames = async (
	analyticsEvent: UIAnalyticsEvent,
	customFieldId: string,
	projectId: string,
	issueTypeId?: string,
): Promise<AssetType[]> => {
	const queryString =
		issueTypeId === null || issueTypeId === undefined
			? qs.stringify({ projectId })
			: qs.stringify({ projectId, issueTypeId });
	return fetchJson(`/rest/assetinternalapi/latest/field/${customFieldId}/config?${queryString}`)
		.then((response: FieldFilter) => response?.types || [])
		.catch((error) => {
			if (error.statusCode === 404) {
				return Promise.resolve([]);
			}

			fireErrorAnalytics({
				event: analyticsEvent,
				error,
				meta: {
					packageName: 'jiraIssueFieldAssets',
					id: 'fetchFieldFilterNames',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
			throw error;
		});
};

/*
 * Search for assets given a name and asset type
 * */
export const fetchAssets = async (
	inputValue = '',
	autoCompleteUrl: string,
	accountId: string | null,
	assetTypesFilter: string[],
	analyticsEvent: UIAnalyticsEvent,
): Promise<AssetOption[]> => {
	const queryParams = {
		prioritizedAssignee: accountId,
		query: inputValue,
		type: assetTypesFilter,
	};
	const queryString = qs.stringify(queryParams);
	return performGetRequest(`${autoCompleteUrl}?${queryString}`)
		.then((response: SuggestionsResponse) => mapAssetEntityToAssetOptions(response.values))
		.catch((error) => {
			fireErrorAnalytics({
				event: analyticsEvent,
				error,
				meta: {
					packageName: 'jiraIssueFieldAssets',
					id: 'fetchAssets',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
			throw error;
		});
};
